import { Typography } from "@/frontend/shared/ui/Typography";
import styles from "./SimilarCompanies.module.scss";
import { EntryPointsIcon, MoveIcon, TagIcon } from "@/frontend/shared/assets/icons";
import { NormalButton } from "@/frontend/shared/ui/Button";
import { useState } from "react";
import { getRouteCompanies } from "@/frontend/shared/const/router";
import { SectionContentWrapper } from "@/frontend/widgets/SectionContentWrapper/SectionContentWrapper";
import { AddCompaniesTagsModal } from "@/frontend/components/AddCompaniesTagsModal/ui/AddCompaniesTagsModal";
import {
  EDataStatus,
  FinancialStage,
  TSimilarCompaniesResponse,
  TTableCompany
} from "@/frontend/features/Company/model/types/types";
import { TableLabels } from "@/frontend/shared/ui/Table/ui/TableLabels";
import { TableBody } from "@/frontend/shared/ui/Table/ui/TableBody/TableBody";
import { InnoveraBlockNoData } from "@/frontend/features/Company/ui/InnoveraBlockNoData/InnoveraBlockNoData";
import { calculateLoadingProgress } from "@/frontend/shared/lib/calculateLoadingProgress/calculateLoadingProgress";
import { AddCompaniesToPipelineModal } from "@/frontend/components/AddCompaniesToPipelineModal/AddCompaniesToPipelineModal";
import { TNewFinancialStage } from "@/libs/models/shared";
import { compressFilter } from "@/frontend/components/FilterV2/lib/filtersEncoding";

export type TSimilarCompaniesProps = {
  onReportError?: () => void;
  isInvestor?: boolean;
  similarCompaniesData?: TSimilarCompaniesResponse;
  isLoadingSimilarCompaniesData: boolean;
  refetchSimilarCompaniesData: () => void;
};

export function SimilarCompanies({
  onReportError,
  isInvestor,
  similarCompaniesData,
  refetchSimilarCompaniesData: triggerRefetch
}: TSimilarCompaniesProps) {
  const similarCompaniesPrompt = similarCompaniesData?.meta.similarCompaniesSearchDescription || "";
  const data = similarCompaniesData?.companies || [];
  const [selectedRowIds, setSelectedRowIds] = useState<number[]>([]);
  const [shownModal, setShownModal] = useState<"tags" | "addToPipeline" | null>(null);
  const isLoading = similarCompaniesData?.status !== EDataStatus.done;
  function handleCardChange(id: number) {
    setSelectedRowIds((currentSelected) => {
      if (!currentSelected.includes(id)) {
        return [...currentSelected, id];
      }
      return currentSelected.filter((cardId) => cardId !== id);
    });
  }

  function selectAllCompanies() {
    if (data?.length === selectedRowIds.length) return setSelectedRowIds([]);
    setSelectedRowIds(data.map((item: { id: number }) => item.id));
  }
  const tableData: TTableCompany[] = data.map((company) => ({
    ...company,
    financialStage: company.financialStage as FinancialStage,
    investors: company.harmonicInvestors?.map((item) => item.name) || [],
    foundingDate: String(company.foundingDate)
  }));
  const content = (
    <div className={styles.contentTableBlockInvestor}>
      <TableLabels
        onChange={selectAllCompanies}
        value={selectedRowIds.length === data?.length && selectedRowIds.length > 0}
        isFounder={!isInvestor}
      />
      <TableBody
        selected={selectedRowIds}
        onChange={handleCardChange}
        companies={tableData}
        isLoading={isLoading}
        triggerRefetch={triggerRefetch}
        isFounder={!isInvestor}
      />
    </div>
  );

  const companiesRoute = getRouteCompanies() + "?clearTags=true";
  const similarCompaniesShowMoreRoute =
    similarCompaniesPrompt.length > 0
      ? companiesRoute +
        "&filter=" +
        compressFilter({
          type: "description",
          isOr: false,
          value: similarCompaniesPrompt,
          operand: "is",
          id: "1"
        })
      : companiesRoute;
  const progressPct = calculateLoadingProgress(
    similarCompaniesData?.status,
    similarCompaniesData?.similarCompaniesSearchStartedAt
  );
  const isProgressBarVisible = similarCompaniesData?.status !== "done" && progressPct <= 100;

  const similarCompaniesContent = (() => {
    if (data?.length > 0 || isLoading) {
      return (
        <div className={styles.innoveraBlock}>
          {isInvestor && (
            <div className={styles.innoveraBlockTitle}>
              <div className={styles.contentActionButtonsBlock}>
                {!!selectedRowIds.length && (
                  <div className={styles.checkboxContainer}>
                    <Typography color="greyscale900" variant="label-4">
                      {selectedRowIds.length} Selected
                    </Typography>
                  </div>
                )}
                <NormalButton
                  size="large"
                  variant="secondary"
                  className={styles.actionButton}
                  onClick={() => setShownModal("addToPipeline")}
                  isDisabled={selectedRowIds.length === 0}
                >
                  <EntryPointsIcon />
                  Add to Pipeline
                </NormalButton>
                <NormalButton
                  size="large"
                  variant="secondary"
                  className={styles.actionButton}
                  isDisabled={selectedRowIds.length === 0}
                  onClick={() => setShownModal("tags")}
                >
                  <TagIcon />
                  Tag
                </NormalButton>
                <AddCompaniesTagsModal
                  triggerRefetch={triggerRefetch}
                  isOpen={shownModal === "tags"}
                  onClose={() => setShownModal(null)}
                  ids={selectedRowIds}
                  currentTags={[]}
                />
                <AddCompaniesToPipelineModal
                  triggerRefetch={() => {}}
                  isOpen={shownModal === "addToPipeline"}
                  onClose={() => {
                    setShownModal(null);
                    setSelectedRowIds([]);
                  }}
                  ids={selectedRowIds}
                />
              </div>
            </div>
          )}
          {content}
          <NormalButton
            variant="primary"
            size="small"
            color="greyscale100"
            className={styles.seeMoreBtn}
            onClick={() => window.open(similarCompaniesShowMoreRoute, "_blank")}
          >
            Show more
            <MoveIcon className={styles.caretDownIcon} />
          </NormalButton>
        </div>
      );
    }
    return <InnoveraBlockNoData />;
  })();
  return (
    <SectionContentWrapper
      isByInnovera
      onReportError={onReportError}
      progress={{ visible: isProgressBarVisible, pct: progressPct }}
    >
      {similarCompaniesContent}
    </SectionContentWrapper>
  );
}
